import { useMemo } from 'react';
import { ITopicFilterState } from 'components/TopicFilters/shared';
import { CountryIds } from 'types/shared';

interface IGlobalFiltersProp {
  charts: number | string;
  country: CountryIds;
  download: number | string;
  streaming: number | string;
}

/**
 * Filters the specified `charts`, a list of chart positions, based on the global country filter.
 * When the global filter is inactive it returns the given `charts` instance.
 *
 * @param {object} charts a list of unfiltered chart positions
 * @param {object} filters the global filter object
 * @returns {array} a memoized list of chart positions
 */
export default function useFilteredCharts(
  charts: any[] | null,
  filters: IGlobalFiltersProp | ITopicFilterState
): any[] | null {
  const filteredCharts = useMemo(
    () => filterCharts({ charts, filters }),
    [charts, filters]
  );

  return filteredCharts;
}

export const filterCharts = ({
  charts,
  filters,
}: {
  charts: any[] | null;
  filters: IGlobalFiltersProp | ITopicFilterState | undefined;
}): any[] | null => {
  if (!charts) return null;

  function filterNoTerritories(chart: any): boolean {
    return chart.territoryData
      ? chart.territoryData.length > 0
      : chart.supportedCountryIds.length > 0;
  }

  function isSameCountryId(territory: any): boolean {
    const territoryString =
      typeof territory === 'string' ? territory : territory.countryId;
    if (Array.isArray(filters?.country)) {
      return !!filters?.country.includes(territoryString);
    }
    return territoryString === filters?.country;
  }

  function isExCountryId(territory: any): boolean {
    const territoryString =
      typeof territory === 'string' ? territory : territory.countryId;

    if (Array.isArray(filters?.country)) {
      const countriesWithReplace = filters?.country.map((country) =>
        country.replace('EX-', '')
      );
      return !!countriesWithReplace?.includes(territoryString);
    }
    return territory.countryId !== filters?.country?.replace('EX-', '');
  }

  function copyAndFilter(chart: any): any {
    const mapFunction = filters?.country?.includes('EX-')
      ? isExCountryId
      : isSameCountryId;

    const copy = Object.assign({}, chart);
    if (copy.territoryData) {
      copy.territoryData = chart.territoryData.filter(mapFunction);
    }
    if (copy.supportedCountryIds) {
      copy.supportedCountryIds = chart.supportedCountryIds.filter(mapFunction);
    }
    return copy;
  }

  if (filters?.country) {
    return charts.map(copyAndFilter).filter(filterNoTerritories);
  }

  return charts;
};
